import { ChordAnalytics } from '@chordcommerce/analytics';

const analytics = typeof window !== 'undefined' && window.analytics;

const getProductUrl = (productHandle, variantId) => {
  if (!productHandle) return
  const base = typeof window !== 'undefined' && window.location.origin
  return `${base}/products/${productHandle}${variantId ? '?variant=' + variantId: ''}`
}

const getChordLineItem = ({ lineItem }) => {
  try {
    const productUrl = getProductUrl(
      lineItem?.merchandise?.product?.handle,
      lineItem?.merchandise?.id?.replace('gid://shopify/ProductVariant/', '')
    )

    return {
      affiliation: 'Vercel',
      brand: lineItem?.merchandise?.product?.vendor || 'Monica + Andy',
      image_url: lineItem?.merchandise?.product?.featuredImage?.url,
      name: lineItem?.merchandise?.product?.title,
      option_values: lineItem?.merchandise?.selectedOptions?.map(o => `${o?.name?.toLowerCase()}:${o?.value}`),
      price: Number(lineItem?.cost?.amountPerQuantity?.amount) || 0,
      product_id: lineItem?.merchandise?.product?.id?.replace('gid://shopify/Product/', ''),
      quantity: lineItem?.quantity,
      sku: lineItem?.merchandise?.sku,
      slug: lineItem?.merchandise?.product?.handle,
      url: productUrl,
      variant: lineItem?.merchandise?.title?.toString(),
      variant_id: lineItem?.merchandise?.id?.replace('gid://shopify/ProductVariant/', ''),
    }
  } catch (error) {
    console.error('getChordLineItem error', error)
    return {}
  }
}

const getChordCheckout = ({ checkout }) => {
  // The Shopify cart is used for checkout data here.
  const cart = checkout

  try {
    const cartProducts = cart?.lines?.map((line, i) => {
      const lineItem = line?.node
      return {
        ...getChordLineItem({ lineItem }),
        position: i + 1
      }
    })

    const coupon = cart?.discountCodes?.[0]

    return {
      affiliation: 'Vercel',
      checkout_type: 'Shopify',
      coupon: coupon?.code,
      currency: 'USD',
      products: cartProducts || [],
      revenue: Number(cart?.cost?.subtotalAmount?.amount) || 0,
      tax: Number(cart?.cost?.totalTaxAmount?.amount) || 0,
      value: Number(cart?.cost?.subtotalAmount?.amount) || 0,
    }
  } catch (error) {
    console.error('getChordCheckout error', error)
    return {}
  }
}

const getChordCart = ({ cart }) => {
  try {
    const cartProducts = cart?.lines?.map((line, i) => {
      const lineItem = line?.node
      return {
        ...getChordLineItem({ lineItem }),
        position: i + 1
      }
    })

    return {
      cart_id: cart?.id?.replace('gid://shopify/Cart/', ''),
      currency: 'USD',
      products: cartProducts,
      value: Number(cart?.cost?.subtotalAmount?.amount) || 0,
    }
  } catch (error) {
    console.error('getChordCart error', error)
    return {}
  }
}

const getChordProduct = ({ position, product, quantity, variantId } = {}) => {
  try {
    const variant = variantId ? product?.variants?.find(v => v.id.toString() === variantId.toString()) : product?.variants?.[0]

    if (!variant) {
      console.error(`getChordProduct error: variant ${variantId} isn't a variant of product ${product?.id}}`)
    }

    const productUrl = getProductUrl(product?.handle, variant?.id)

    // Ensure SKU is always provided with fallbacks
    let sku = variant?.sku;
    if (!sku && variant?.id) {
      // Fallback to variant ID if no SKU
      sku = `VARIANT-${variant.id}`;
    }
    if (!sku) {
      // Final fallback
      sku = 'NO-SKU';
    }

    return {
      affiliation: 'Vercel',
      brand: product?.vendor || 'Monica + Andy',
      category: variant?.initial_color?.replace(/\//g, ' '),
      category2: variant?.initial_size || 'nosize',
      compare_at_price: variant?.compare_at_price,
      image_url: variant?.image,
      name: product?.title,
      option_values: variant?.merged_options,
      position,
      price: Number(variant?.price) || 0,
      product_id: product?.id?.toString(),
      quantity,
      sku: sku,
      slug: product?.handle,
      url: productUrl,
      variant: variant?.title,
      variant_id: variant?.id?.toString(),
    }
  } catch (error) {
    console.error('getChordProduct error', error)
    return {}
  }
}

export const chord = new ChordAnalytics({
  debug: import.meta.env.DEV, // Set to `true` to see Chord event validation and errors in the console.
  cdp: analytics,
  formatters: {
    objects: {
      cart: getChordCart,
      checkout: getChordCheckout,
      lineItem: getChordLineItem,
      product: getChordProduct,
    }
  },
  metadata: {
    i18n: {
      currency: 'USD',
      locale: 'en-us',
    },
    ownership: {
      omsId: import.meta.env.PUBLIC_CHORD_OMS_ID,
      storeId: import.meta.env.PUBLIC_CHORD_OMS_ID,
      tenantId: import.meta.env.PUBLIC_CHORD_TENANT_ID,
    },
    platform: {
      name: 'Shopify',
      type: 'web',
    },
    store: {
      domain: import.meta.env.PUBLIC_SHOPIFY_DOMAIN
    }
  }
})
